import { html, nothing } from "lit-html";
import type { View, App } from "../app.js";
import { api } from "../api.js";
import type { Scorecards, Trend, CountyMap, Opportunity, QueryResult, ScorecardRow, RepTicket } from "../types.js";
import { targetKpis, pacePill, meter, sectionHead, delta, oppItem, repPageRows, kpi, docCell } from "../components.js";
import { money, moneyShort, initials, pct, fmtDate } from "../format.js";
import { icons } from "../icons.js";
import { hbars } from "../charts.js";
import { trendChart } from "./overview.js";
import "../map.js";

type LineKey = "loose" | "air" | "aptech";
type Slices = Record<LineKey, Scorecards>;
type ListData = { kind: "list"; sc: Scorecards; slices: Slices };
type DetailData = { kind: "detail"; repId: number; sc: Scorecards; slices: Slices; trend: Trend; map: CountyMap; opps: Opportunity[]; customers: QueryResult; tickets: RepTicket[] | null };

/** The three groups on the Reps page, and which sales count toward each. */
export const LINES: { key: LineKey; title: string; short: string; q: Record<string, string> }[] = [
  { key: "loose", title: "Loose Goods reps", short: "Loose Goods", q: { division: "loose" } },
  { key: "air", title: "Supplied Air techs", short: "Supplied Air (SCBA + compressors)", q: { subdivision: "service/scba,service/compressors" } },
  { key: "aptech", title: "Apparatus techs", short: "Apparatus service", q: { subdivision: "service/apparatus" } },
];
let group: "all" | LineKey = "all";
/** Hired in the last 120 days (from NetSuite). */
const isNew = (hired: string | null) => !!hired && Date.now() - Date.parse(hired + "T00:00:00") < 120 * 86400_000;
let ticketLine: "all" | LineKey = "all";
const loadSlices = async (app: App, extra: Record<string, string | number | null>) => {
  const [loose, air, aptech] = await Promise.all(LINES.map((l) => api.scorecards(app.q({ ...extra, division: null, ...l.q }))));
  return { loose: loose!, air: air!, aptech: aptech! };
};
type Data = ListData | DetailData;

let sortBy: "ytd" | "pace" | "gap" | "name" = "ytd";
let selectedCounty: string | null = null;

function sorted(rows: ScorecardRow[]): ScorecardRow[] {
  const r = rows.filter((x) => x.repId !== null);
  const val = (x: ScorecardRow) => sortBy === "ytd" ? x.total.ytd : sortBy === "pace" ? x.total.pace ?? -1 : x.total.gap ?? Number.MAX_SAFE_INTEGER; // no target yet: sort last
  if (sortBy === "name") return r.sort((a, b) => a.repName.localeCompare(b.repName));
  return r.sort((a, b) => (sortBy === "gap" ? val(a) - val(b) : val(b) - val(a)));
}

export const reps: View<Data> = {
  title: (app) => (app.param ? app.me.reps.find((r) => String(r.id) === app.param)?.name ?? "Rep" : "Reps"),
  filters: ["year", "manager"],
  async load(app, param) {
    if (!param) {
      selectedCounty = null;
      const [sc, slices] = await Promise.all([api.scorecards(app.q({ rep: null, division: null })), loadSlices(app, { rep: null })]);
      return { kind: "list", sc, slices };
    }
    const repId = Number(param);
    if (!app.me.reps.some((r) => r.id === repId)) throw new Error("That rep isn't on your team.");
    // The Reps page has no division picker, so never carry one in from another page.
    const q = app.q({ rep: repId, manager: null, division: null });
    const { from, to } = app.yearRange();
    const filters = { rep: [repId] };
    const [sc, trend, map, opps, customers, slices, tickets] = await Promise.all([
      api.scorecards(q), api.trend(q), api.map(q), api.opportunities(q),
      api.query({ dims: ["customer"], metrics: ["revenue"], filters, range: "custom", from, to, compare: "prior_year", limit: 12 }),
      loadSlices(app, { rep: repId, manager: null }),
      api.repTickets(app.q({ rep: repId, manager: null, division: null, line: ticketLine })),
    ]);
    return { kind: "detail", repId, sc, slices, trend, map, opps: opps.items, customers, tickets: tickets.items };
  },
  render(app: App, d) {
    return d.kind === "list" ? list(app, d) : detail(app, d);
  },
};

let search = "";

function list(app: App, d: ListData) {
  const all = repPageRows(d.sc.rows, true);
  const q = search.trim().toLowerCase();
  const hit = (r: ScorecardRow) => !q || [r.repName, r.title ?? "", r.repLabel, r.territory.join(" ")].join(" ").toLowerCase().includes(q);
  const people = all.filter((r) => !r.shared && hit(r));
  // A team run through a shared account (Mindie's apparatus sales all post to "Apparatus Sales") still shows that account.
  const accounts = !all.some((r) => !r.shared) ? all.filter((r) => r.shared && hit(r)) : [];
  const set = (x: typeof sortBy) => { sortBy = x; app.render(); };
  const lines = group === "all" ? LINES : LINES.filter((l) => l.key === group);
  const card = (r: ScorecardRow, line: LineKey, whole = false) => {
    const sr = whole ? r : d.slices[line].rows.find((y) => y.repId === r.repId);
    const x = sr?.total;
    const other = LINES.filter((l) => l.key !== line && r.groups.includes(l.key));
    return html`<a class="glass rep-card" href=${app.href("reps", String(r.repId))} @click=${() => { ticketLine = line; }}>
      <div class="rep-top"><div class="avatar">${initials(r.repName)}</div>
        <div style="min-width:0;flex:1"><div class="rep-name">${r.repName}${isNew(r.hired) ? html`<span class="tag" title=${`Hired ${fmtDate(r.hired!)}`}>New hire</span>` : nothing}</div>
          <div class="rep-meta">${[r.title, r.repLabel].filter(Boolean).join(" · ")}</div></div>
        ${pacePill(x?.pace ?? null, true)}</div>
      <div class="rep-mix">${other.length ? html`<span class="tag" style="margin:0 6px 0 0">Also ${other.map((o) => o.title).join(", ")}</span>` : nothing}${[r.territory.join(" · "), r.mix.map((m) => `${m.name} ${m.pct}%`).join(" · ")].filter(Boolean).join("  ·  ")}</div>
      ${x ? meter(x) : html`<div class="meter"></div>`}
      <div class="rep-nums">
        <div><small>YTD</small><b>${moneyShort(x?.ytd ?? 0)}</b></div>
        <div><small>Target</small><b>${moneyShort(x?.target ?? null)}</b></div>
        <div><small>Projected</small><b>${moneyShort(x?.projected ?? null)}</b></div>
        <div title="Gross profit this year (NetSuite estimated GP)"><small>GP</small><b>${sr?.gp == null ? "—" : moneyShort(sr.gp)}${sr?.gpPct != null ? html`<span class="gp-pct">${pct(sr.gpPct)}</span>` : nothing}</b></div>
      </div>
    </a>`;
  };
  const sections = lines.map((l) => {
    const members = sorted(people.filter((r) => r.groups.includes(l.key)).map((r) => {
      const x = d.slices[l.key].rows.find((y) => y.repId === r.repId);
      return x ? { ...r, total: x.total } : { ...r, total: { ...r.total, ytd: 0, pace: null, gap: null } };
    }));
    return { ...l, members, ytd: members.reduce((a, r) => a + r.total.ytd, 0) };
  }).filter((x) => x.members.length);
  return html`
    <div class="row" style="margin-bottom:14px;flex-wrap:wrap;gap:12px">
      <div class="seg" role="group" aria-label="Group">
        <button aria-pressed=${group === "all"} @click=${() => { group = "all"; app.render(); }}>All</button>
        ${LINES.map((l) => html`<button aria-pressed=${group === l.key} @click=${() => { group = l.key; app.render(); }}>${l.title}</button>`)}
      </div>
      <div class="seg" role="group" aria-label="Sort">
        ${(["ytd", "pace", "gap", "name"] as const).map((x) => html`<button aria-pressed=${sortBy === x} @click=${() => set(x)}>${{ ytd: "Revenue", pace: "Pace", gap: "Furthest behind", name: "Name" }[x]}</button>`)}
      </div>
      <input class="input" type="search" placeholder="Find a rep, title or state" style="width:220px" .value=${search}
        @input=${(e: Event) => { search = (e.target as HTMLInputElement).value; app.render(); }}>
      <span class="spacer"></span>
      <span class="muted small">target = last year +${Math.round(d.sc.growth * 100)}%</span>
    </div>
    ${sections.length ? sections.map((sec) => html`
      <div class="row" style="margin:22px 0 10px;align-items:baseline;gap:10px">
        <h3 class="section-title">${sec.title}</h3><span class="muted small">${sec.members.length} · ${moneyShort(sec.ytd)} this year in ${sec.short}</span>
      </div>
      <div class="grid g-3">${sec.members.map((r) => card(r, sec.key))}</div>`) : accounts.length ? nothing : html`<div class="glass card empty-state">${q ? `No reps match "${search}".` : "No reps in this view."}</div>`}
    ${accounts.length ? html`<div class="row" style="margin:22px 0 10px;align-items:baseline;gap:10px"><h3 class="section-title">Team accounts</h3><span class="muted small">This team's sales post to a shared NetSuite account</span></div>
      <div class="grid g-3">${sorted(accounts).map((r) => card(r, LINES[0]!.key, true))}</div>` : nothing}
    <p class="muted small mt">Each card counts only that group's work: Loose Goods sales, Supplied Air (SCBA and compressor service), or Apparatus service. Someone who does both kinds of service shows in both, with each part counted separately.</p>`;
}

function detail(app: App, d: DetailData) {
  const row = d.sc.rows.find((r) => r.repId === d.repId);
  const t = row?.total ?? d.sc.total;
  const divs = app.me.divisions.filter((x) => row?.byDivision[x.key]);
  const terrCounties = d.map.counties.filter((c) => c.reps.includes(d.repId));
  const top = [...terrCounties].sort((a, b) => b.ytd - a.ytd).slice(0, 10);
  const sel = selectedCounty ? d.map.counties.find((c) => c.fips === selectedCounty) : null;
  return html`
    <div class="row" style="margin-bottom:14px;gap:14px;flex-wrap:wrap;align-items:center"><a class="btn ghost" href=${app.href("reps")}>${icons.arrowLeft} All reps</a>
      <span class="muted">${[row?.title, row?.repLabel, row?.territory.join(" · ")].filter(Boolean).join(" · ")}${row && isNew(row.hired) ? html` <span class="tag">New hire · ${fmtDate(row.hired!)}</span>` : nothing}</span></div>
    <div class="grid g-kpi">${targetKpis(t, d.sc.growth)}
      ${kpi("Gross profit YTD", row?.gp == null ? "—" : moneyShort(row.gp), html`<span>${row?.gpPct != null ? `${pct(row.gpPct, 1)} GP margin` : "No GP on these lines"}</span>`, "NetSuite's estimated gross profit on this year's invoices, less credits")}</div>

    <div class="grid g-2 mt">
      <section class="glass card">
        ${sectionHead("Monthly revenue", `${d.trend.year} vs ${d.trend.year - 1}`)}
        ${trendChart(d.trend, d.sc.growth, 250)}
      </section>
      <section class="glass card">
        ${sectionHead("By division", "YTD vs target")}
        ${divs.length ? html`<div class="panel-list">${divs.map((dv) => {
          const x = row!.byDivision[dv.key]!;
          return html`<div class="opp" style="grid-template-columns:1fr auto">
            <div><div class="opp-title">${dv.name}</div><div class="opp-detail">${money(x.ytd)} · ${delta(x.ytd, x.priorYtd)} · target ${moneyShort(x.target)}</div><div style="margin-top:8px">${meter(x)}</div></div>
            <div>${pacePill(x.pace, true)}</div></div>`;
        })}</div>` : html`<div class="empty-state">No sales this year.</div>`}
      </section>
    </div>

    ${linesOfWork(app, d, row)}

    <div class="grid g-2 mt">
      <section class="glass card">
        ${sectionHead("Territory", `${terrCounties.length} counties with sales in the last 2 years · outlined`, html`<a class="link" href=${app.href("map", undefined, { rep: d.repId, manager: null })}>Open full map</a>`)}
        <ff-map .data=${{
          counties: d.map.counties, metric: "revenue", territoryRepIds: [d.repId], showLowConversion: true, selected: selectedCounty, height: window.innerWidth < 760 ? 340 : 420,
          fitKey: `rep-${d.repId}-${d.map.year}`,
          onSelect: (f: string | null) => { selectedCounty = f; app.render(); },
        }}></ff-map>
        ${sel ? html`<div class="mt panel-list">
          <div class="card-title" style="margin-bottom:6px">${sel.label}</div>
          <div class="kv"><span>Revenue YTD</span><b>${money(sel.ytd)}</b></div>
          <div class="kv"><span>Same period last year</span><b>${money(sel.priorYtd)}</b></div>
          <div class="kv"><span>Quotes won (12 mo)</span><b>${sel.quotesResolved ? `${pct(sel.conversion)} (${sel.quotesWon}/${sel.quotesResolved})` : "—"}</b></div>
        </div>` : nothing}
      </section>
      <section class="glass card">
        ${sectionHead("Top counties", "This year · tick = same period last year")}
        ${hbars(top.map((c) => ({ label: c.label, value: c.ytd, ghost: c.priorYtd, onClick: () => { selectedCounty = c.fips; app.render(); } })), moneyShort, { ghostName: "Last year" })}
        <div class="mt">${sectionHead("Top customers", "This year")}</div>
        ${hbars(d.customers.rows.slice(0, 8).map((r) => ({ label: r.label.customer ?? "", value: r.values.revenue ?? 0, ghost: r.prior?.revenue ?? null, onClick: r.key.customer ? () => app.go("customer", String(r.key.customer)) : undefined })), moneyShort, { ghostName: "Last year" })}
      </section>
    </div>

    <section class="glass card mt">
      ${sectionHead("Opportunities", `${d.opps.length} flagged for ${row?.repName ?? "this rep"}`, d.opps.length > 12 ? html`<a class="link" href=${app.href("opportunities", undefined, { rep: d.repId, manager: null })}>See all ${d.opps.length}</a>` : undefined)}
      ${d.opps.length ? d.opps.slice(0, 12).map((o) => oppItem(o, (f) => { selectedCounty = f; app.render(); window.scrollTo({ top: 0, behavior: "smooth" }); })) : html`<div class="empty-state">Nothing flagged.</div>`}
    </section>`;
}

function linesOfWork(app: App, d: DetailData, row: ScorecardRow | undefined) {
  const have = LINES.map((l) => { const r = d.slices[l.key].rows.find((y) => y.repId === d.repId); return { ...l, t: r?.total, gp: r?.gp ?? null, gpPct: r?.gpPct ?? null }; }).filter((l) => l.t && (l.t.ytd !== 0 || l.t.priorFull !== 0));
  const pick = async (k: "all" | LineKey) => {
    ticketLine = k; d.tickets = null; app.render();
    try { d.tickets = (await api.repTickets(app.q({ rep: d.repId, manager: null, division: null, line: k }))).items; } catch { d.tickets = []; }
    app.render();
  };
  const tickets = d.tickets;
  return html`<section class="glass card mt">
    ${sectionHead("Work by line", `${row?.repName ?? "This rep"}'s ${d.trend.year} split into Loose Goods, Supplied Air and Apparatus service`)}
    ${have.length ? html`<div class="grid g-3" style="margin-bottom:16px">${have.map((l) => html`<div class="line-box ${ticketLine === l.key ? "on" : ""}" @click=${() => pick(l.key)}>
        <div class="row" style="align-items:center"><b>${l.short}</b><span class="spacer"></span>${pacePill(l.t!.pace, true)}</div>
        <div class="kpi-value" style="font-size:24px;margin:6px 0 2px">${money(l.t!.ytd)}</div>
        <div class="opp-detail">${delta(l.t!.ytd, l.t!.priorYtd)} vs ${moneyShort(l.t!.priorYtd)} same time last year · target ${moneyShort(l.t!.target)}</div>
        <div class="opp-detail">GP ${l.gp == null ? "—" : money(l.gp)}${l.gpPct != null ? ` · ${pct(l.gpPct, 1)}` : ""}</div>
        <div style="margin-top:8px">${meter(l.t!)}</div>
      </div>`)}</div>` : nothing}
    <div class="row" style="margin-bottom:10px;flex-wrap:wrap;gap:10px">
      <div class="seg" role="group" aria-label="Tickets">
        <button aria-pressed=${ticketLine === "all"} @click=${() => pick("all")}>All tickets</button>
        ${have.map((l) => html`<button aria-pressed=${ticketLine === l.key} @click=${() => pick(l.key)}>${l.short}</button>`)}
      </div>
      <span class="spacer"></span><span class="muted small">${tickets ? `${tickets.length} this year · ${money(tickets.reduce((a, x) => a + x.amount, 0))}` : "Loading…"}</span>
    </div>
    ${!tickets ? html`<div class="empty-state">Loading…</div>` : tickets.length === 0 ? html`<div class="empty-state">No tickets here this year.</div>` : html`
      <div class="table-wrap" style="max-height:460px"><table class="t">
        <thead><tr><th>Sales order · invoice</th><th>Date</th><th>Customer</th><th>Work</th><th class="r">Amount</th></tr></thead>
        <tbody>${tickets.map((x) => html`<tr>
          <td class="name" style="white-space:nowrap">${docCell(x)}</td>
          <td>${fmtDate(x.date)}</td>
          <td><a class="plain" href=${`#/customer/${encodeURIComponent(x.customerId)}`}>${x.customerName}</a></td>
          <td class="muted" style="max-width:280px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title=${x.work.join(", ")}>${x.work.join(", ")}</td>
          <td class="r">${money(x.amount)}</td></tr>`)}</tbody>
      </table></div>`}
  </section>`;
}
